import * as React from 'react';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import {
    DatagridConfigurable,
    List,
    TextField,
    TextInput,
    TopToolbar,
    useListContext,
    useNotify,
    useRecordContext,
} from 'react-admin';
import {
    Divider,
    Tabs,
    Tab,
    CircularProgress,
    Button,
    Box,
    InputAdornment,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import PurchasedPrice from './PurchasedPrice';
import OrderItemsFiled from './OrderItemField';
import { goApi } from '../../services';
import UserField from '../../common/UserField';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';

const ListActions = () => (
    <TopToolbar className={'justify-start w-full'}>
        <FilterForm />
    </TopToolbar>
);

const Actions = () => {
    const [revalidate, setRevalidate] = React.useState('');
    const [revalidating, setRevalidating] = React.useState(false);
    const record = useRecordContext();
    const notify = useNotify();

    const handleRevalidate = async () => {
        try {
            setRevalidating(true);

            try {
                const data = await goApi.put<any>(`/ecom/orders/${record?.id}/re-validate`);

                notify('validated successfully', {
                    type: 'success',
                    messageArgs: {},
                });

                setRevalidate(data?.revalidate);
            } catch (err: any) {
                const message = err?.data?.message || err?.message || 'please try again';
                notify('please try again', {
                    type: 'error',
                    messageArgs: { Error: message },
                });
            }
        } catch (error) {
            // Handle fetch error
            console.error('Fetch error:', error);
            notify('error in validating', {
                type: 'error',
                messageArgs: {},
            });
        } finally {
            setRevalidating(false);
        }
    };

    return (
        <div className="w-full">
            {/* {revalidate && <div>{revalidate}</div>} */}
            {revalidating ? (
                <div className="flex justify-center">
                    <CircularProgress />
                </div>
            ) : (
                <Button
                    onClick={handleRevalidate}
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                >
                    Re-validate
                </Button>
            )}
        </div>
    );
};

const OrderList = () => {
    const record = useRecordContext();

    return (
        <List
            filterDefaultValues={{ status: 'PENDING' }}
            sort={{ field: 'created_at', order: 'DESC' }}
            perPage={25}
            actions={<ListActions />}
        >
            <TabbedDatagrid />
        </List>
    );
};

const FilterForm = () => {
    const { displayedFilters, filterValues, setFilters, hideFilter } = useListContext();
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const prevFilters = useRef<any>({});

    const form = useForm({
        defaultValues: filterValues,
    });

    const { watch } = form;
    const formValues = watch();

    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            const v: any = {
                status: filterValues.status, 
            };

            if (formValues.trackingId) {
                v['trackingId'] = formValues.trackingId;
            }
            if (formValues.name) {
                v['name'] = formValues.name;
            }
            if (formValues.product) {
                v['product'] = formValues.product;
            }
             if (JSON.stringify(prevFilters.current) !== JSON.stringify(v)) {
            prevFilters.current = v;
            setFilters(v, undefined, false); }
        }, 700);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [formValues.trackingId, formValues.name, formValues.product, setFilters, filterValues.status]);

    const clearFilters = () => {
        form.reset({
            trackingId: '',
            name: '',
            product: '',
        });
        setFilters({}, undefined, false);
    };

    const onSubmit = (values: any) => {
        const v: any = {
            status: filterValues.status, 
        };

        if (values.trackingId) {
            v['trackingId'] = values.trackingId;
        }
        if (values.name) {
            v['name'] = values.name;
        }
        if (values.product) {
            v['product'] = values.product;
        }
        setFilters( undefined, false ); 
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Box display="flex" alignItems={'flex-end'} mb={1}>
                    <Box component="span" mr={2} mb={1.5}>
                        <Button
                            variant="outlined"
                            color="primary"
                            type="button"
                            onClick={clearFilters}
                        >
                            Clear
                        </Button>
                    </Box>
                    <Box component="span" mr={2} mb={1.5}>
                        <Button variant="outlined" color="primary" type="submit">
                            Filter
                        </Button>
                    </Box>
                    <Box component="span" mr={2}>
                        <TextInput
                            resettable
                            helperText={false}
                            source="trackingId"
                            label="Tracking Id"
                            placeholder={'Tracking Id'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position={'start'}>
                                        <SearchIcon color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box component="span" mr={2}>
                        <TextInput
                            resettable
                            helperText={false}
                            source="name"   
                            label="User"
                            placeholder="Enter name or phone"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box component="span" mr={2}>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};

const tabs = [
    { id: 'DELIVERED', name: 'DELIVERED' },
    { id: 'PENDING', name: 'PENDING' },
    { id: 'RECEIVED', name: 'RECEIVED' },
    { id: 'FAILED', name: 'FAILED' },
];

const TabbedDatagrid = () => {
    const listContext = useListContext();
    const { filterValues, setFilters, displayedFilters } = listContext;

    const handleChange = useCallback(
        (event: React.ChangeEvent<{}>, value: any) => {
            setFilters &&
                setFilters(
                    { ...filterValues, status: value },
                    displayedFilters,
                    false, // no debounce, we want the filter to fire immediately
                );
        },
        [displayedFilters, filterValues, setFilters],
    );

    return (
        <Fragment>
            <ClearFiltersOnRouteChange />
            <Tabs
                variant="fullWidth"
                centered
                value={filterValues.status}
                indicatorColor="primary"
                onChange={handleChange}
            >
                {tabs.map(choice => (
                    <Tab
                        key={choice?.id}
                        label={
                            <span>
                                {choice.name}
                            </span>
                        }
                        value={choice.id}
                    />
                ))}
            </Tabs>
            <Divider />
            <>
                <DatagridConfigurable bulkActionButtons={false} omit={[]}>
                    <UserField source="student" label="Student" />
                    <TextField source="trackingId" />
                    <TextField source="status" />
                    <PurchasedPrice label="Amount" />
                    <OrderItemsFiled />
                    <UserField source="createdBy" label="Created By" />

                    <FormattedDateTimeField source="createdAt" />
                    <div className="w-[110px]">
                        {['PENDING'].includes(filterValues.status) && <Actions />}
                    </div>
                </DatagridConfigurable>
            </>
        </Fragment>
    );
};

export default OrderList;
